import { useEffect, useState } from 'react';
import { Box, Button, Drawer, IconButton, styled } from '@mui/material';
import { IconLayoutSidebarRight, IconX } from '@tabler/icons-react';

import { useLocation } from 'react-router-dom';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useAppStore from 'src/stores/app';
import Filters from 'src/widgets/FeedFilter/Desktop';
import MenuComponent from 'src/widgets/Menu/Menu';
import Header from 'src/widgets/Header';

interface IDesktopLayoutProps {
    children: React.ReactNode;
    hideRightSidebar?: boolean;
    withMainContentWrapper?: boolean;
    rightSidebar?: React.ReactNode;
    rightSidebarTitle?: string;
    rightSidebarWidth?: number;
}

const RightSidebarContainer = styled(Box)(() => ({
    height: '100vh',
    overflow: 'auto',
    position: 'sticky',
    top: 0,
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'var(--lm-canvas)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'var(--lm-scrollbar)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: 'var(--lm-muted)',
    },
}));

export default function DesktopLayout({
    children,
    hideRightSidebar = false,
    withMainContentWrapper,
    rightSidebar,
    rightSidebarTitle,
    rightSidebarWidth,
}: IDesktopLayoutProps) {
    const location = useLocation();
    const showRightSidebar = !hideRightSidebar && (!!rightSidebar || location.pathname === '/feed');
    const isMobile = useIsMobileSize();
    const [contextOpen, setContextOpen] = useState(false);
    useEffect(() => {
        setContextOpen(false);
    }, [location.pathname, location.search]);
    const isSidebarCollapsed = useAppStore((state) => state.isSidebarCollapsed);

    if (isMobile)
        return (
            <Box
                bgcolor="background.paper"
                minHeight="100dvh"
                pb="100px"
            >
                <Header white />
                {rightSidebar && rightSidebarTitle && showRightSidebar && (
                    <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            startIcon={<IconLayoutSidebarRight size={18} />}
                            aria-expanded={contextOpen}
                            onClick={() => setContextOpen(true)}
                        >
                            {rightSidebarTitle}
                        </Button>
                    </Box>
                )}
                {children}
                {rightSidebar && showRightSidebar && (
                    <Drawer
                        anchor="right"
                        open={contextOpen}
                        onClose={() => setContextOpen(false)}
                        PaperProps={{ sx: { width: 'min(360px, 100vw)', bgcolor: 'background.default' } }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                            <IconButton
                                aria-label="Закрыть контекст"
                                onClick={() => setContextOpen(false)}
                            >
                                <IconX />
                            </IconButton>
                        </Box>
                        {rightSidebar}
                    </Drawer>
                )}
            </Box>
        );

    return (
        <Box
            display="grid"
            gridTemplateColumns={{
                md: `${isSidebarCollapsed ? 72 : 280}px minmax(0,1fr)${
                    showRightSidebar ? ` ${rightSidebarWidth || 280}px` : ''
                }`,
                lg: `${isSidebarCollapsed ? 72 : 300}px minmax(0,1fr)${
                    showRightSidebar ? ` ${rightSidebarWidth || 330}px` : ''
                }`,
            }}
            bgcolor="background.paper"
            minHeight="100dvh"
            maxWidth={1920}
            margin="auto"
            sx={{
                transition: 'grid-template-columns 0.28s cubic-bezier(0.2, 0, 0, 1)',
                willChange: 'grid-template-columns',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
        >
            <MenuComponent
                open={true}
                anchore="left"
                variant="permanent"
                onClose={() => {}}
            />

            <Box
                px={{ md: 3, lg: 5, xl: 7 }}
                py={5}
                pb="120px"
                minWidth={0}
            >
                {withMainContentWrapper ? <MainContentWrapper>{children}</MainContentWrapper> : children}
            </Box>
            {showRightSidebar && (
                <RightSidebarContainer sx={{ pb: '120px' }}>
                    {rightSidebar ? rightSidebar : <Filters />}
                </RightSidebarContainer>
            )}
        </Box>
    );
}

interface IMainContentWrapperProps {
    children: React.ReactNode;
}

const Wrapper = styled(Box)(({ theme }) => ({
    padding: '1rem',
    background: theme.palette.background.default,
    height: '100%',
}));

export const MainContentWrapper = ({ children }: IMainContentWrapperProps) => {
    return <Wrapper>{children}</Wrapper>;
};
